import React, { useEffect, useReducer, useState } from "react";

import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { Pencil, Trash2, Plus, AlertTriangle, Search } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../../components/ui/tooltip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import DepartmentModal from "../../components/DepartmentModal";
import ConfirmationModal from "../../components/ConfirmationModal";

const reducer = (state, action) => {
	if (action.type === "LOAD_DEPARTMENTS") {
		const departments = action.payload;
		const newDepartments = [];

		departments.forEach((department) => {
			const departmentIndex = state.findIndex((d) => d.id === department.id);
			if (departmentIndex !== -1) {
				state[departmentIndex] = department;
			} else {
				newDepartments.push(department);
			}
		});

		return [...state, ...newDepartments];
	}

	if (action.type === "UPDATE_DEPARTMENTS") {
		const department = action.payload;
		const departmentIndex = state.findIndex((d) => d.id === department.id);

		if (departmentIndex !== -1) {
			state[departmentIndex] = department;
			return [...state];
		} else {
			return [department, ...state];
		}
	}

	if (action.type === "DELETE_DEPARTMENT") {
		const departmentId = action.payload;
		const departmentIndex = state.findIndex((d) => d.id === departmentId);
		if (departmentIndex !== -1) {
			state.splice(departmentIndex, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

const Departments = () => {
	const [departments, dispatch] = useReducer(reducer, []);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [pageNumber, setPageNumber] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [reloadVersion, setReloadVersion] = useState(0);

	const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
	const [selectedDepartment, setSelectedDepartment] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [searchParam, reloadVersion]);

	useEffect(() => {
		setLoading(true);
		const delayDebounceFn = setTimeout(async () => {
			try {
				const { data } = await api.get("/departments", {
					params: { searchParam, pageNumber },
				});
				dispatch({ type: "LOAD_DEPARTMENTS", payload: data.departments });
				setHasMore(data.hasMore);
			} catch (err) {
				toastError(err);
			} finally {
				setLoading(false);
			}
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, pageNumber, reloadVersion]);

	useEffect(() => {
		const socket = openSocket();

		const onDepartment = (data) => {
			if (data.action === "update" || data.action === "create") {
				dispatch({ type: "UPDATE_DEPARTMENTS", payload: data.department });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_DEPARTMENT", payload: +data.departmentId });
			}
		};

		socket.on("department", onDepartment);

		return () => {
			try { socket.off("department", onDepartment); } catch (_) {}
		};
	}, []);

	const handleSearch = (event) => {
		setSearchParam(event.target.value.toLowerCase());
	};

	const handleOpenDepartmentModal = () => {
		setDepartmentModalOpen(true);
		setSelectedDepartment(null);
	};

	const handleCloseDepartmentModal = () => {
		setDepartmentModalOpen(false);
		setSelectedDepartment(null);
		setSearchParam("");
		setPageNumber(1);
		setReloadVersion((v) => v + 1);
	};

	const handleEditDepartment = (department) => {
		setSelectedDepartment(department);
		setDepartmentModalOpen(true);
	};

	const handleCloseConfirmationModal = () => {
		setConfirmModalOpen(false);
		setSelectedDepartment(null);
	};

	const handleDeleteDepartment = async (departmentId) => {
		try {
			await api.delete(`/departments/${departmentId}`);
			toast.success("Departamento excluído com sucesso!");
		} catch (err) {
			toastError(err);
		}
		setConfirmModalOpen(false);
		setSelectedDepartment(null);
		setSearchParam("");
		setPageNumber(1);
		setReloadVersion((v) => v + 1);
	};

	const loadMore = () => {
		setPageNumber((prevState) => prevState + 1);
	};

	const handleScroll = (e) => {
		if (!hasMore || loading) return;
		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			loadMore();
		}
	};

	return (
		<MainContainer>
			<ConfirmationModal
				title={
					selectedDepartment &&
					`Apagar o departamento ${selectedDepartment.name}?`
				}
				open={confirmModalOpen}
				onClose={handleCloseConfirmationModal}
				onConfirm={() => handleDeleteDepartment(selectedDepartment.id)}
			>
				Só o nome do departamento é apagado. Nenhuma pessoa, equipe ou conversa
				é afetada. Não dá para desfazer.
			</ConfirmationModal>
			<DepartmentModal
				open={departmentModalOpen}
				onClose={handleCloseDepartmentModal}
				departmentId={selectedDepartment?.id}
			/>
			<MainHeader>
				<Title>Departamentos</Title>
				<MainHeaderButtonsWrapper>
					<div className="relative">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="Pesquisar..."
							type="search"
							value={searchParam}
							onChange={handleSearch}
							className="w-56 pl-9"
						/>
					</div>
					<Button onClick={handleOpenDepartmentModal}>
						<Plus className="h-4 w-4" />
						Criar um departamento
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<div
				className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6"
				onScroll={handleScroll}
			>
				<div className="mb-4 max-w-3xl space-y-3">
					<p className="text-sm leading-relaxed text-muted-foreground">
						Departamento agrupa Equipes, Usuários e Filas. Ao criar um
						atendimento, ele herda automaticamente o departamento da fila de
						entrada. Use o filtro na lista lateral de atendimentos para ver só
						o setor que você quer. Apagar um departamento não apaga ninguém
						nem nenhum atendimento.
					</p>
				</div>
				<div className="rounded-xl border bg-card">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Nome do departamento</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{departments.map((department) => (
								<TableRow key={department.id}>
									<TableCell className="font-medium">
										{department.name}
									</TableCell>
									<TableCell>
										<TooltipProvider>
											<div className="flex items-center justify-center gap-1">
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8"
															onClick={() => handleEditDepartment(department)}
														>
															<Pencil className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>Trocar o nome</TooltipContent>
												</Tooltip>
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-destructive hover:text-destructive"
															onClick={() => {
																setSelectedDepartment(department);
																setConfirmModalOpen(true);
															}}
														>
															<Trash2 className="h-4 w-4" />
														</Button>
													</TooltipTrigger>
													<TooltipContent>Apagar</TooltipContent>
												</Tooltip>
											</div>
										</TooltipProvider>
									</TableCell>
								</TableRow>
							))}
							{loading && <TableRowSkeleton columns={3} />}
							{!loading && departments.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={3}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										Nenhum departamento cadastrado — e o sistema funciona
										normalmente assim.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</MainContainer>
	);
};

export default Departments;
